// src/services/pocketbaseApi.js
import { io } from 'socket.io-client';
import { getPersonalKey } from '../identity';

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'; // Adjust if your backend runs on a different port

// Setup socket.io instance
const socket = io(apiUrl);

// Fetch all records (GET)
export async function fetchDemoRecords() {
    const res = await fetch(`${apiUrl}/api/pocketbase-demo`);
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
}

// Add a record (POST)
export async function addDemoRecord(data) {
    const res = await fetch(`${apiUrl}/api/pocketbase-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
}

// Update a record (PUT)
export async function updateDemoRecord(id, data) {
    const res = await fetch(`${apiUrl}/api/pocketbase-demo/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
}

// Remove a record (DELETE)
export async function removeDemoRecord(id) {
    const res = await fetch(`${apiUrl}/api/pocketbase-demo/${id}`, {
        method: 'DELETE'
    });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
}

// Subscribe to realtime updates (Socket.io)
export function subscribeDemoCollection(callback) {
    socket.on('demo_collection_update', callback);
    return () => socket.off('demo_collection_update', callback);
}

// --- ROOM SOCKET.IO SUPPORT ---
// Subscribe to real-time updates for a specific room's demo_collection
export function subscribeRoomDemoCollection(roomId, callback) {
    socket.emit('join_room', roomId);
    const event = `room_demo_collection_update_${roomId}`;
    socket.on(event, callback);
    return () => {
        socket.emit('leave_room', roomId);
        socket.off(event, callback);
    };
}

// --- ROOM MEMBERS API ---
export async function createRoomMember(roomId, username) {
    const res = await fetch(`${apiUrl}/api/room/${roomId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
    });
    if (!res.ok) throw new Error('Failed to create room member');
    return res.json();
}

export async function removeRoomMember(roomId, username) {
    const res = await fetch(`${apiUrl}/api/room/${roomId}/members/${username}`, {
        method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to remove room member');
    return res.json();
}

export { socket };

// --- POLL API FUNCTIONS ---

// Create a new poll
export async function createPoll(pollData) {
    const res = await fetch(`${apiUrl}/api/polls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Polley-Key': getPersonalKey() },
        body: JSON.stringify(pollData)
    });
    if (!res.ok) throw new Error(`Failed to create poll: ${res.status}`);
    return res.json();
}

// Join a poll (create participant) - updated to use slug endpoint
export async function joinPoll(pollSlug, participantData) {
    const res = await fetch(`${apiUrl}/api/polls/slug/${pollSlug}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Polley-Key': getPersonalKey() },
        body: JSON.stringify(participantData)
    });
    if (!res.ok) {
        let errorMessage = `Failed to join poll: ${res.status}`;
        try {
            const resClone = res.clone();
            const errorData = await resClone.json();
            errorMessage = errorData.error || errorMessage;
        } catch (jsonErr) {
            try {
                const raw = await res.text();
                console.error('Raw error response:', raw);
            } catch (textErr) {
                // ignore
            }
        }
        throw new Error(errorMessage);
    }
    return res.json();
}

// Leave a poll (remove participant)
export async function leavePoll(pollId, participantName) {
    const res = await fetch(`${apiUrl}/api/polls/${pollId}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: participantName })
    });
    if (!res.ok) throw new Error(`Failed to leave poll: ${res.status}`);
    return res.json();
}

// Add a poll item
export async function addPollItem(pollId, itemData) {
    const res = await fetch(`${apiUrl}/api/polls/${pollId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
    });
    if (!res.ok) throw new Error(`Failed to add poll item: ${res.status}`);
    return res.json();
}

// Cast a vote
export async function castVote(voteData) {
    const res = await fetch(`${apiUrl}/api/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(voteData)
    });
    if (!res.ok) throw new Error(`Failed to cast vote: ${res.status}`);
    return res.json();
}

// Get poll details with items and participants
export async function getPoll(pollId) {
    const res = await fetch(`${apiUrl}/api/polls/${pollId}`);
    if (!res.ok) throw new Error(`Failed to get poll: ${res.status}`);
    return res.json();
}

// Get poll by slug
export async function getPollBySlug(slug) {
    const res = await fetch(`${apiUrl}/api/polls/slug/${slug}`);
    if (!res.ok) {
        let errorMessage = `Failed to get poll by slug: ${res.status}`;
        try {
            const resClone = res.clone();
            const errorData = await resClone.json();
            errorMessage = errorData.error || errorMessage;
        } catch (jsonErr) {
            try {
                const raw = await res.text();
                console.error('Raw error response:', raw);
            } catch (textErr) {
                // ignore
            }
        }
        throw new Error(errorMessage);
    }
    return res.json();
}

export async function removePollItem(pollId, pollItemId) {
    const res = await fetch(`${apiUrl}/api/polls/${pollId}/items/${pollItemId}`, {
        method: 'DELETE',
    });
    if (!res.ok) {
        throw new Error('Failed to remove poll item');
    }
    return await res.json();
}


// Polls linked to this browser's personal key. Claims are the names this browser
// already uses, so they get linked to the key on the way.
export async function getMyPolls(claims = []) {
    const res = await fetch(`${apiUrl}/api/me/polls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Polley-Key': getPersonalKey() },
        body: JSON.stringify({ claims })
    });
    if (!res.ok) throw new Error(`Failed to load your polls: ${res.status}`);
    return res.json();
}
